import { QueryClient } from '@tanstack/vue-query'

/**
 * The app's one query client, and with it the cache policy every query inherits: fresh for
 * five minutes, kept for thirty, refetched when the tab regains focus, and retried only
 * where retrying can help.
 *
 * A component reaches it through `useQueryClient()`. Import this module directly only from
 * outside the component tree — a router guard, which runs long before any component and can
 * prime a query with `fetchQuery` so the `use…()` that renders a moment later reads the
 * cache instead of asking again.
 *
 * It is a module-level singleton in `src/shared`, so it decides the policy for **both**
 * apps. The defaults are written out rather than left implicit for that reason.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Ask again only when asking again can help: a refusal below 500 will be refused
      // identically, while a dropped connection or a server fault often will not.
      retry: (failureCount: number, error: unknown) => {
        const status = (error as { response?: { status?: number } })?.response
          ?.status

        if (status !== undefined && status < 500) return false

        return failureCount < 2
      },
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: false
      // Do not set `refetchOnMount: false` here. TanStack checks it before `isStale()`,
      // which is the only thing that reads `isInvalidated`, so a query invalidated while it
      // was unmounted would serve stale cache for the whole `gcTime` — a record changed
      // from a list would then open in its edit modal still showing the old value.
    }
  }
})
